import React, { useState } from 'react';
import {
  API_URL,
  DEFAULT_INDOOR_RATE_CENTS,
  DEFAULT_OUTDOOR_RATE_CENTS,
  getAuthToken,
  getVenueQuery
} from './internalIntegrationUtils';

interface Court {
  id: string;
  name: string;
  type: string;
  sport: string;
  isActive: boolean;
  hourlyRateCents: number;
}

type Step = 'form' | 'loading' | 'review' | 'saving' | 'success' | 'error';

export default function SmashParkIntegration({ onClose }: { onClose: () => void }) {
  const [step, setStep] = useState<Step>('form');
  const [locationUid, setLocationUid] = useState('');
  const [categoryUid, setCategoryUid] = useState('');
  const [courtType, setCourtType] = useState<'outdoor' | 'indoor'>('outdoor');
  const [courtName, setCourtName] = useState('Court');
  const [error, setError] = useState('');
  const [previewResult, setPreviewResult] = useState<any>(null);
  const [editableCourts, setEditableCourts] = useState<Court[]>([]);

  const token = getAuthToken();

  const handlePreview = async () => {
    if (!locationUid.trim() || !categoryUid.trim()) {
      setError('Location UID and Category UID are required.');
      return;
    }
    setError('');
    setStep('loading');

    try {
      const res = await fetch(`${API_URL}/api/integrations/smashpark/preview${getVenueQuery()}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          locationUid: locationUid.trim(),
          categoryUid: categoryUid.trim(),
          courtType,
          courtName: courtName.trim() || 'Court'
        })
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);

      const defaultRate = courtType === 'outdoor' ? DEFAULT_OUTDOOR_RATE_CENTS : DEFAULT_INDOOR_RATE_CENTS;
      const courts: Court[] = (data.data?.courts || []).map((c: any) => ({
        ...c,
        hourlyRateCents: defaultRate
      }));

      setPreviewResult(data);
      setEditableCourts(courts);
      setStep('review');
    } catch (err: any) {
      setError(err.message || 'Preview failed');
      setStep('form');
    }
  };

  const handleCourtNameChange = (index: number, name: string) => {
    const updated = [...editableCourts];
    updated[index] = { ...updated[index], name };
    setEditableCourts(updated);
  };

  const handleCourtRateChange = (index: number, cents: number) => {
    const updated = [...editableCourts];
    updated[index] = { ...updated[index], hourlyRateCents: cents };
    setEditableCourts(updated);
  };

  const handleImport = async () => {
    setStep('saving');
    setError('');

    try {
      const res = await fetch(`${API_URL}/api/integrations/import${getVenueQuery()}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          integrationType: 'smashpark',
          externalId: `${locationUid.trim()}::${categoryUid.trim()}`,
          config: {
            locationUid: locationUid.trim(),
            categoryUid: categoryUid.trim(),
            courtType,
            courtName: courtName.trim() || 'Court'
          },
          courts: editableCourts,
          schedule: [],
          operatingHours: { open: null, close: null },
          rawAvailability: previewResult?.raw?.availability || []
        })
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);

      setStep('success');
    } catch (err: any) {
      setError(err.message || 'Import failed');
      setStep('review');
    }
  };

  if (step === 'success') {
    return (
      <div className="integration-setup">
        <div className="integration-setup__header">
          <button className="integration-setup__back" onClick={onClose}>← Back</button>
          <h2>Smash Park</h2>
        </div>
        <div className="integration-setup__success">
          <div className="integration-setup__success-icon">✓</div>
          <h3>Connected!</h3>
          <p>Smash Park availability is now syncing.</p>
          <button className="btn btn-primary" onClick={onClose}>Done</button>
        </div>
      </div>
    );
  }

  return (
    <div className="integration-setup">
      <div className="integration-setup__header">
        <button className="integration-setup__back" onClick={onClose}>← Back</button>
        <h2>Smash Park</h2>
        <span className="integration-badge" style={{ background: '#6366f1', color: '#fff', fontSize: '11px', padding: '2px 8px', borderRadius: '12px' }}>Internal</span>
      </div>

      {step === 'form' || step === 'loading' ? (
        <div className="integration-setup__body">
          <p className="integration-setup__description">
            Connect a Smash Park pickleball category to sync real-time court availability.
            Find the IDs in the reservation URL: <code>/&#123;locationUid&#125;/reservations/category/&#123;categoryUid&#125;/select-day</code>
          </p>

          <div className="integration-setup__field">
            <label>Location UID</label>
            <input
              type="text"
              value={locationUid}
              onChange={e => setLocationUid(e.target.value)}
              placeholder="e.g. Aez3fKwytvLsox3gDfWC"
              disabled={step === 'loading'}
            />
            <span className="integration-setup__field-hint">
              La Vista, NE: Aez3fKwytvLsox3gDfWC &nbsp;·&nbsp;
              Roseville, MN: D2VKAp7DVyxLYquzx64p &nbsp;·&nbsp;
              W. Des Moines: H2tYbiZh2SGdjCj6r9Jf &nbsp;·&nbsp;
              Westerville, OH: snmeWwrgw2QscjmMZ02S
            </span>
          </div>

          <div className="integration-setup__field">
            <label>Category UID</label>
            <input
              type="text"
              value={categoryUid}
              onChange={e => setCategoryUid(e.target.value)}
              placeholder="e.g. kkajeaC4KeqgdRuKpPCz"
              disabled={step === 'loading'}
            />
            <span className="integration-setup__field-hint">
              La Vista outdoor: kkajeaC4KeqgdRuKpPCz &nbsp;·&nbsp;
              La Vista indoor: 4MySQEPzeTSv5RNXMNs8 &nbsp;·&nbsp;
              Roseville outdoor: DETdIXyXbQbdCH6Qeg7N &nbsp;·&nbsp;
              Roseville indoor: IQgJOhFKzg0qL0i3u13V &nbsp;·&nbsp;
              WDM outdoor: rOLY2Kgprw3HsRD73Ast &nbsp;·&nbsp;
              WDM indoor: rpFcIxrDKMMhhhjyHz3M &nbsp;·&nbsp;
              Westerville outdoor: dW6SUoNdzUUJ7wU28RTW &nbsp;·&nbsp;
              Westerville indoor: d4WTFBgKOyDJzk8MbSev
            </span>
          </div>

          <div className="integration-setup__row">
            <div className="integration-setup__field">
              <label>Court Type</label>
              <select value={courtType} onChange={e => setCourtType(e.target.value as 'outdoor' | 'indoor')} disabled={step === 'loading'}>
                <option value="outdoor">Outdoor</option>
                <option value="indoor">Indoor</option>
              </select>
            </div>
            <div className="integration-setup__field">
              <label>Court Name Prefix</label>
              <input
                type="text"
                value={courtName}
                onChange={e => setCourtName(e.target.value)}
                placeholder="Court"
                disabled={step === 'loading'}
              />
            </div>
          </div>

          {error && <div className="integration-setup__error">{error}</div>}

          <div className="integration-setup__actions">
            <button className="btn btn-primary" onClick={handlePreview} disabled={step === 'loading'}>
              {step === 'loading' ? 'Loading...' : 'Preview'}
            </button>
            <button className="btn btn-secondary" onClick={onClose} disabled={step === 'loading'}>
              Cancel
            </button>
          </div>
        </div>
      ) : step === 'review' || step === 'saving' ? (
        <div className="integration-setup__body">
          <p className="integration-setup__description">
            Found <strong>{editableCourts.length} court{editableCourts.length !== 1 ? 's' : ''}</strong>.
            Review and edit names and rates before connecting.
          </p>

          <div className="integration-setup__courts">
            {editableCourts.map((court, i) => (
              <div key={i} className="integration-setup__court-row">
                <input
                  type="text"
                  value={court.name}
                  onChange={e => handleCourtNameChange(i, e.target.value)}
                  className="integration-setup__court-name"
                  disabled={step === 'saving'}
                />
                <span className="integration-setup__court-type">{court.type}</span>
                <div className="integration-setup__court-rate">
                  <span>$</span>
                  <input
                    type="number"
                    value={court.hourlyRateCents / 100}
                    onChange={e => handleCourtRateChange(i, Math.round(parseFloat(e.target.value) * 100))}
                    min={0}
                    step={1}
                    className="integration-setup__rate-input"
                    disabled={step === 'saving'}
                  />
                  <span>/hr</span>
                </div>
              </div>
            ))}
          </div>

          {error && <div className="integration-setup__error">{error}</div>}

          <div className="integration-setup__actions">
            <button className="btn btn-primary" onClick={handleImport} disabled={step === 'saving'}>
              {step === 'saving' ? 'Connecting...' : 'Connect'}
            </button>
            <button className="btn btn-secondary" onClick={() => setStep('form')} disabled={step === 'saving'}>
              Back
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
